import React, { memo, ReactNode, useCallback, useEffect, useRef } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Text } from '../Text';
import { TabsVariant, useTabsContext } from './Tabs';
interface TabProps {
  /** Unique value for this tab */
  value: string;
  /** Tab label text */
  label: string;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Data ID for testing */
  dataId?: string;
  /** Icon element to display before label */
  icon?: ReactNode;
  /** Variant passed from parent Tabs component */
  variant?: TabsVariant;
}
const TabButton = /*#__PURE__*/styled('button')({
  name: "TabButton",
  class: "t1l7kirp",
  propsAsIs: false
});
const TabContent = /*#__PURE__*/styled('div')({
  name: "TabContent",
  class: "t8mzijv",
  propsAsIs: false
});
const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i1widirl",
  propsAsIs: false
});
const LabelWrapper = /*#__PURE__*/styled('div')({
  name: "LabelWrapper",
  class: "l1n4e1v8",
  propsAsIs: false
});
const TabComponent = ({
  value,
  label,
  disabled = false,
  className,
  dataId,
  icon,
  variant = 'scrollable'
}: TabProps) => {
  const {
    activeTab,
    onTabChange,
    registerTab,
    unregisterTab
  } = useTabsContext();
  const tabRef = useRef<HTMLButtonElement>(null);
  const isActive = activeTab === value;

  // Register/unregister tab element for indicator positioning
  useEffect(() => {
    if (tabRef.current) {
      registerTab(value, tabRef.current);
    }
    return () => {
      unregisterTab(value);
    };
  }, [value, registerTab, unregisterTab]);
  const handleClick = useCallback(() => {
    if (!disabled) {
      onTabChange(value);
    }
  }, [disabled, onTabChange, value]);
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);
  return <TabButton ref={tabRef} className={cx(className, variant, isActive && 'active')} disabled={disabled} onClick={handleClick} onKeyDown={handleKeyDown} role="tab" aria-selected={isActive} aria-disabled={disabled} tabIndex={disabled ? -1 : 0} data-id={dataId} type="button">
      <TabContent>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <LabelWrapper className={variant}>
          <Text variant="labelS">{label}</Text>
        </LabelWrapper>
      </TabContent>
    </TabButton>;
};
export const Tab = memo(TabComponent);

require("./Tab.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Tab.tsx");